import { localeRequestHeaders } from './localePreference';
import { apiErrorMessage } from './apiErrorMessages';

const API_PREFIX = '/api/v1';

async function apiRequest(path, user, options = {}) {
  const token = await user.getIdToken();
  const isFormData = options.body instanceof FormData;
  const { timeoutMs = 30000, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  let response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      ...fetchOptions,
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        ...localeRequestHeaders(),
        ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw new Error(apiErrorMessage('learningTimeout'));
    throw new Error(apiErrorMessage('learningNetwork'));
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || apiErrorMessage('learningRequest'));
  }
  return response.json();
}

export function getLearningAssessment({ user, stepNumber, generationId, retry = false }) {
  const params = new URLSearchParams();
  if (generationId) params.set('generationId', generationId);
  if (retry) params.set('retry', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiRequest(`/learning-pathways/steps/${stepNumber}/assessment${query}`, user, {
    method: 'GET',
    timeoutMs: 45000,
  });
}

export function saveLearningAssessmentDraft({ user, stepNumber, taskType, responses, aiUseDisclosure }) {
  return apiRequest(`/learning-pathways/steps/${stepNumber}/draft`, user, {
    method: 'PUT',
    body: JSON.stringify({ taskType, responses, aiUseDisclosure }),
  });
}

export function submitGuidedPractice({ user, stepNumber }) {
  return apiRequest(`/learning-pathways/steps/${stepNumber}/guided-practice/submit`, user, {
    method: 'POST',
    timeoutMs: 45000,
  });
}

export function submitWorkSample({ user, stepNumber }) {
  return apiRequest(`/learning-pathways/steps/${stepNumber}/work-sample/submit`, user, {
    method: 'POST',
    timeoutMs: 45000,
  });
}

export function uploadWorkSamplePdf({ user, stepNumber, file }) {
  const body = new FormData();
  body.append('file', file);
  return apiRequest(`/learning-pathways/steps/${stepNumber}/work-sample/upload`, user, {
    method: 'POST',
    body,
    timeoutMs: 90000,
  });
}

export function startLearningAssessmentDefense({ user, stepNumber }) {
  return apiRequest(`/learning-pathways/steps/${stepNumber}/defense/start`, user, {
    method: 'POST',
    timeoutMs: 60000,
  });
}

export function answerLearningAssessmentDefense({ user, stepNumber, questionId, answer }) {
  return apiRequest(`/learning-pathways/steps/${stepNumber}/defense/answer`, user, {
    method: 'POST',
    body: JSON.stringify({ questionId, answer }),
    timeoutMs: 60000,
  });
}

export function ensureLearningPathway({ user, survey }) {
  return apiRequest('/learning-pathways/generate', user, {
    method: 'POST',
    body: JSON.stringify(survey || null),
  });
}

export function updateLearningPathwayProgress({ user, action, stepNumber, courseId, exerciseIndex }) {
  return apiRequest('/learning-pathways/progress', user, {
    method: 'PATCH',
    body: JSON.stringify({
      action,
      ...(stepNumber ? { stepNumber } : {}),
      ...(courseId ? { courseId } : {}),
      ...(exerciseIndex ? { exerciseIndex } : {}),
    }),
  });
}

export function submitCourseCompletionProof({ user, stepNumber, courseId, courseTitle, proofUrl, file }) {
  const body = new FormData();
  body.append('courseId', courseId);
  body.append('courseTitle', courseTitle || '');
  body.append('proofUrl', proofUrl || '');
  if (file) body.append('file', file);
  return apiRequest(`/learning-pathways/steps/${stepNumber}/course-proof`, user, {
    method: 'POST',
    body,
    timeoutMs: 90000,
  });
}

export function certificateDownloadUrl({ name, skill, verifyUrl }) {
  const params = new URLSearchParams({
    name: name || 'JOBSEEQR User',
    skill: skill || 'Learning Pathway Certificate',
    verifyUrl: verifyUrl || 'https://employer.jobseeqr.co/verify-cert/pending',
  });
  return `https://us-central1-jobseeqr.cloudfunctions.net/generateCertificate?${params.toString()}`;
}
